// Training Room — debug sandbox for testing champion interactions.
// Left sidebar: pick a champion. Right sidebar: pick an enemy type + level.
// Click a hex in the player zone (bottom) to place/remove champions.
// Click a hex in the enemy zone (top) to place/remove enemies.
// Press FIGHT to launch a real combat and return here afterward.

import type { Scene } from "../engine/scene/SceneManager";
import type { Game } from "../game/Game";
import { HexGrid } from "../engine/hex/HexGrid";
import { HexLayout } from "../engine/hex/HexLayout";
import { GRID_COLS, GRID_ROWS, zoneHexUrl } from "../config/constants";
import { isPlayerZone } from "../game/RunState";
import { CHAMPIONS, TIER_COLORS, type ChampionId } from "../data/champions";
import { ENEMIES, type EnemyId } from "../data/enemies";
import { championIconUrl } from "../config/constants";
import { drawSprite } from "../engine/render/Sprite";
import { buildEnemyUnit, buildGhostUnit } from "../combat/UnitFactory";
import { hexKey, type HexCoord } from "../engine/hex/HexCoord";
import { RunState } from "../game/RunState";
import { XP_FOR_LEVEL } from "../units/UnitStats";
import { CombatScene } from "./CombatScene";
import type { MapNode } from "../map/CampaignMap";

const SIDEBAR_W = 155;
const FOOTER_H = 58;
const MIN_HEX_SIZE = 14;
const MAX_HEX_SIZE = 36;
const ENEMY_ROWS = 4; // rows 0–3 = enemy zone, rows 4–7 = player zone
const FAKE_NODE: MapNode = { id: "training", type: "minion", layer: 0, indexInLayer: 0, x: 0.5, y: 0.5 };

type AnyPlacement =
  | { kind: "champion"; id: ChampionId; level: number }
  | { kind: "enemy"; id: EnemyId; level: number };

export class TrainingScene implements Scene {
  private root!: HTMLDivElement;
  private layout!: HexLayout;
  private hexSize = 24;

  private playerPlacements = new Map<string, AnyPlacement>();
  private enemyPlacements = new Map<string, AnyPlacement>();
  private selectedChampion: ChampionId | null = "Garen";
  private selectedEnemy: EnemyId | null = "minion";
  private champLevel = 1;
  private enemyLevel = 1;
  private hoverHex: HexCoord | null = null;

  private fightBtn!: HTMLButtonElement;
  private champLevelEl!: HTMLSpanElement;
  private enemyLevelEl!: HTMLSpanElement;
  private champCountEl!: HTMLSpanElement;
  private enemyCountEl!: HTMLSpanElement;

  private cleanupPointer?: () => void;
  private cleanupResize?: () => void;

  constructor(private game: Game) {}

  enter(): void {
    this.computeLayout();
    this.root = document.createElement("div");
    this.root.className = "training-root";
    // Force pointer-events: none so the full-screen overlay div doesn't swallow
    // canvas clicks. The `#scene-root > *` CSS rule would otherwise override the
    // class rule and make this div block every click in the center area.
    this.root.style.pointerEvents = "none";
    this.root.innerHTML = this.buildHTML();
    this.game.sceneRoot.appendChild(this.root);

    this.fightBtn = this.root.querySelector<HTMLButtonElement>("#training-fight")!;
    this.champLevelEl = this.root.querySelector<HTMLSpanElement>("#champ-level-val")!;
    this.enemyLevelEl = this.root.querySelector<HTMLSpanElement>("#enemy-level-val")!;
    this.champCountEl = this.root.querySelector<HTMLSpanElement>("#champ-count")!;
    this.enemyCountEl = this.root.querySelector<HTMLSpanElement>("#enemy-count")!;

    this.attachSidebarListeners();
    this.attachFooterListeners();

    const grid = new HexGrid<null>(GRID_COLS, GRID_ROWS);
    const offDown = this.game.input.onPointerDown((e) => this.onCanvasClick(e.x, e.y));
    const offMove = this.game.input.onPointerMove((e) => {
      const hex = this.layout.pixelToHex({ x: e.x, y: e.y });
      this.hoverHex = grid.inBounds(hex) ? hex : null;
    });
    this.cleanupPointer = () => { offDown(); offMove(); };

    this.cleanupResize = this.game.renderer.onResize(() => {
      this.computeLayout();
    });

    this.refreshFightBtn();
  }

  exit(): void {
    this.cleanupPointer?.();
    this.cleanupResize?.();
    this.root.remove();
  }

  update(_dt: number): void {}

  render(ctx: CanvasRenderingContext2D): void {
    this.drawGrid(ctx);
    this.drawZoneLabels(ctx);
    this.drawHover(ctx);
    this.drawPlacements(ctx);
  }

  // ---- HTML construction ----

  private buildHTML(): string {
    const champCards = (Object.keys(CHAMPIONS) as ChampionId[]).map((id) => {
      const def = CHAMPIONS[id];
      const color = TIER_COLORS[def.tier];
      const sel = this.selectedChampion === id ? "selected" : "";
      return `
        <div class="tr-card ${sel}" data-champ="${id}" title="${def.displayName}">
          <div class="tr-icon" style="background-image:url('${championIconUrl(def.iconId)}');border-color:${color}"></div>
          <div class="tr-label" style="color:${color}">${def.displayName}</div>
        </div>`;
    }).join("");

    const enemyCards = (Object.keys(ENEMIES) as EnemyId[]).map((id) => {
      const def = ENEMIES[id];
      const sel = this.selectedEnemy === id ? "selected" : "";
      return `
        <div class="tr-card ${sel}" data-enemy="${id}" title="${def.displayName}">
          <div class="tr-icon" style="background-image:url('${def.iconPath}')"></div>
          <div class="tr-label">${def.displayName}</div>
        </div>`;
    }).join("");

    return `
      <div class="tr-sidebar tr-sidebar-left">
        <div class="tr-sidebar-head">
          YOUR TEAM
          <span class="tr-count" id="champ-count">0</span>
        </div>
        <div class="tr-level-row">
          <span class="tr-dim">Lvl</span>
          <button class="tr-lvl-btn" id="champ-lvl-minus">−</button>
          <span class="tr-lvl-val" id="champ-level-val">1</span>
          <button class="tr-lvl-btn" id="champ-lvl-plus">+</button>
        </div>
        <div class="tr-list">${champCards}</div>
      </div>

      <div class="tr-sidebar tr-sidebar-right">
        <div class="tr-sidebar-head">
          ENEMIES
          <span class="tr-count" id="enemy-count">0</span>
        </div>
        <div class="tr-level-row">
          <span class="tr-dim">Lvl</span>
          <button class="tr-lvl-btn" id="enemy-lvl-minus">−</button>
          <span class="tr-lvl-val" id="enemy-level-val">1</span>
          <button class="tr-lvl-btn" id="enemy-lvl-plus">+</button>
        </div>
        <div class="tr-list">${enemyCards}</div>
      </div>

      <div class="tr-footer">
        <button class="btn btn-sm ghost" id="training-clear">CLEAR</button>
        <div class="tr-footer-center">
          <div class="tr-footer-title">TRAINING ROOM</div>
          <div class="tr-footer-hint">Click a hex to place · Click again to remove</div>
        </div>
        <button class="btn btn-sm" id="training-fight" disabled>FIGHT!</button>
      </div>
    `;
  }

  private attachSidebarListeners(): void {
    // Champion cards
    this.root.querySelectorAll<HTMLElement>("[data-champ]").forEach((el) => {
      el.addEventListener("click", () => {
        const id = el.dataset.champ as ChampionId;
        this.selectedChampion = this.selectedChampion === id ? null : id;
        this.selectedEnemy = null;
        this.refreshCardSelection();
      });
    });

    // Enemy cards
    this.root.querySelectorAll<HTMLElement>("[data-enemy]").forEach((el) => {
      el.addEventListener("click", () => {
        const id = el.dataset.enemy as EnemyId;
        this.selectedEnemy = this.selectedEnemy === id ? null : id;
        this.selectedChampion = null;
        this.refreshCardSelection();
      });
    });

    // Champion level buttons
    this.root.querySelector("#champ-lvl-minus")!.addEventListener("click", () => {
      this.champLevel = Math.max(1, this.champLevel - 1);
      this.champLevelEl.textContent = String(this.champLevel);
    });
    this.root.querySelector("#champ-lvl-plus")!.addEventListener("click", () => {
      this.champLevel = Math.min(18, this.champLevel + 1);
      this.champLevelEl.textContent = String(this.champLevel);
    });

    // Enemy level buttons
    this.root.querySelector("#enemy-lvl-minus")!.addEventListener("click", () => {
      this.enemyLevel = Math.max(1, this.enemyLevel - 1);
      this.enemyLevelEl.textContent = String(this.enemyLevel);
    });
    this.root.querySelector("#enemy-lvl-plus")!.addEventListener("click", () => {
      this.enemyLevel = Math.min(18, this.enemyLevel + 1);
      this.enemyLevelEl.textContent = String(this.enemyLevel);
    });
  }

  private attachFooterListeners(): void {
    this.root.querySelector("#training-clear")!.addEventListener("click", () => {
      this.playerPlacements.clear();
      this.enemyPlacements.clear();
      this.refreshFightBtn();
    });

    this.fightBtn.addEventListener("click", () => this.startFight());
  }

  private refreshCardSelection(): void {
    this.root.querySelectorAll<HTMLElement>("[data-champ]").forEach((el) => {
      el.classList.toggle("selected", el.dataset.champ === this.selectedChampion);
    });
    this.root.querySelectorAll<HTMLElement>("[data-enemy]").forEach((el) => {
      el.classList.toggle("selected", el.dataset.enemy === this.selectedEnemy);
    });
  }

  private refreshFightBtn(): void {
    const pc = this.playerPlacements.size;
    const ec = this.enemyPlacements.size;
    this.fightBtn.disabled = pc === 0 || ec === 0;
    this.champCountEl.textContent = String(pc);
    this.enemyCountEl.textContent = String(ec);
  }

  // ---- Canvas interaction ----

  private onCanvasClick(cx: number, cy: number): void {
    const hex = this.layout.pixelToHex({ x: cx, y: cy });
    const grid = new HexGrid<null>(GRID_COLS, GRID_ROWS);
    if (!grid.inBounds(hex)) return;

    const { row } = HexGrid.axialToOffset(hex);
    const key = hexKey(hex);
    const inEnemyZone = row < ENEMY_ROWS;

    const map = inEnemyZone ? this.enemyPlacements : this.playerPlacements;

    if (map.has(key)) {
      map.delete(key);
    } else if (this.selectedChampion) {
      map.set(key, { kind: "champion", id: this.selectedChampion, level: this.champLevel });
    } else if (this.selectedEnemy) {
      map.set(key, { kind: "enemy", id: this.selectedEnemy, level: this.enemyLevel });
    }

    this.refreshFightBtn();
  }

  // ---- Fight launch ----

  private startFight(): void {
    if (this.playerPlacements.size === 0 || this.enemyPlacements.size === 0) return;

    const state = new RunState(Math.floor(Math.random() * 0x7fffffff));
    let rosterId = 0;

    for (const [key, placement] of this.playerPlacements) {
      const pos = keyToHex(key);
      if (placement.kind === "champion") {
        const xp = XP_FOR_LEVEL[placement.level - 1] ?? 0;
        state.roster.push({ id: `tr_${rosterId++}`, championId: placement.id, xp, stars: 1, currentHp: null, pos, bonuses: { hpMult: 1, adMult: 1 }, itemId: null });
      }
      // enemy-kind in player zone: not a supported combo, skip
    }

    const enemyUnits = [...this.enemyPlacements.entries()].map(([key, placement]) => {
      const pos = keyToHex(key);
      if (placement.kind === "champion") {
        const xp = XP_FOR_LEVEL[placement.level - 1] ?? 0;
        const entry = { id: `tr_ghost_${rosterId++}`, championId: placement.id, xp, stars: 1 as const, currentHp: null, pos, bonuses: { hpMult: 1, adMult: 1 }, itemId: null };
        return buildGhostUnit(entry, pos.q, placement.level);
      }
      return buildEnemyUnit(placement.id, pos.q, placement.level);
    });

    state.pendingCombat = { node: FAKE_NODE, enemies: enemyUnits, fighterLimit: null };
    this.game.runState = state;

    this.game.sceneManager.transition(
      new CombatScene(this.game, FAKE_NODE, (_winner) => {
        this.game.runState = null;
        this.game.sceneManager.transition(this);
      }),
    );
  }

  // ---- Layout ----

  private computeLayout(): void {
    const w = this.game.renderer.designW;
    const h = this.game.renderer.designH;
    const availW = Math.max(60, w - SIDEBAR_W * 2 - 8);
    const availH = Math.max(120, h - FOOTER_H - 8);
    const sizeFromW = availW / (Math.sqrt(3) * (GRID_COLS + 0.5));
    const sizeFromH = availH / (1.5 * (GRID_ROWS - 1) + 2);
    this.hexSize = Math.min(MAX_HEX_SIZE, Math.max(MIN_HEX_SIZE, Math.min(sizeFromW, sizeFromH)));
    const gridPixelW = this.hexSize * Math.sqrt(3) * (GRID_COLS + 0.5);
    const gridPixelH = this.hexSize * (1.5 * (GRID_ROWS - 1) + 2);
    const originX = SIDEBAR_W + (availW - gridPixelW) / 2 + (this.hexSize * Math.sqrt(3)) / 2;
    const originY = (h - FOOTER_H - gridPixelH) / 2 + this.hexSize;
    this.layout = new HexLayout({ size: this.hexSize, origin: { x: originX, y: originY } });
  }

  // ---- Rendering ----

  private drawGrid(ctx: CanvasRenderingContext2D): void {
    const grid = new HexGrid<null>(GRID_COLS, GRID_ROWS);
    const coords = [...grid.coords()];
    const imgW = this.layout.width;
    const imgH = this.layout.height;

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const coord of coords) {
      for (const c of this.layout.hexCorners(coord)) {
        if (c.x < minX) minX = c.x;
        if (c.x > maxX) maxX = c.x;
        if (c.y < minY) minY = c.y;
        if (c.y > maxY) maxY = c.y;
      }
    }
    const sample = this.game.assets.get(zoneHexUrl(true));
    const extraBottom = sample.naturalWidth > 0
      ? ((sample.naturalHeight * imgW) / sample.naturalWidth - imgH + 10) * 1.1
      : 0;

    ctx.save();
    ctx.beginPath();
    ctx.rect(minX, minY, maxX - minX, maxY - minY + Math.max(0, extraBottom));
    ctx.clip();

    // Pass 1: top rows first so bottom rows paint on top.
    // Player zone uses teamHex (blue), enemy zone uses enemyHex (red).
    for (const coord of coords) {
      const center = this.layout.hexToPixel(coord);
      const img = this.game.assets.get(zoneHexUrl(isPlayerZone(coord)));
      const drawH = ((img.naturalWidth > 0 ? (img.naturalHeight * imgW) / img.naturalWidth : imgH) + 10) * 1.1;
      const drawW = imgW * 1.1;
      ctx.drawImage(img, center.x - drawW / 2, center.y - imgH * 1.1 / 2, drawW, drawH);
    }

    ctx.restore();
  }

  private drawZoneLabels(ctx: CanvasRenderingContext2D): void {
    const w = this.game.renderer.designW;

    // Divider line between row 3 and row 4
    const rowAhex = HexGrid.offsetToAxial(0, 3);
    const rowBhex = HexGrid.offsetToAxial(0, 4);
    const yA = this.layout.hexToPixel(rowAhex).y + this.hexSize * 0.87;
    const yB = this.layout.hexToPixel(rowBhex).y - this.hexSize * 0.87;
    const divY = (yA + yB) / 2;

    ctx.save();
    ctx.strokeStyle = "#2a3446";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.beginPath();
    ctx.moveTo(SIDEBAR_W, divY);
    ctx.lineTo(w - SIDEBAR_W, divY);
    ctx.stroke();
    ctx.setLineDash([]);

    // Zone labels
    ctx.font = "bold 9px 'Inter', sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const cx = w / 2;
    const enemyCenterRow = HexGrid.offsetToAxial(2, 1);
    const playerCenterRow = HexGrid.offsetToAxial(2, GRID_ROWS - 2);
    const ey = this.layout.hexToPixel(enemyCenterRow).y;
    const py = this.layout.hexToPixel(playerCenterRow).y;

    ctx.fillStyle = "rgba(200,65,61,0.35)";
    ctx.fillText("ENEMY ZONE", cx, ey - this.hexSize * 2.5);
    ctx.fillStyle = "rgba(74,158,255,0.35)";
    ctx.fillText("YOUR TEAM", cx, py + this.hexSize * 2.5);
    ctx.restore();
  }

  private drawHover(ctx: CanvasRenderingContext2D): void {
    if (!this.hoverHex) return;
    const corners = this.layout.hexCorners(this.hoverHex);
    ctx.save();
    ctx.globalAlpha = 0.3;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) ctx.lineTo(corners[i].x, corners[i].y);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  private drawPlacements(ctx: CanvasRenderingContext2D): void {
    const sz = this.hexSize * 1.4;

    for (const [key, placement] of this.playerPlacements) {
      const pos = keyToHex(key);
      const { x, y } = this.layout.hexToPixel(pos);
      const icon = placement.kind === "champion"
        ? championIconUrl(CHAMPIONS[placement.id].iconId)
        : ENEMIES[placement.id].iconPath;
      this.drawUnitOnHex(ctx, x, y, sz, "player", icon);
    }

    for (const [key, placement] of this.enemyPlacements) {
      const pos = keyToHex(key);
      const { x, y } = this.layout.hexToPixel(pos);
      const icon = placement.kind === "champion"
        ? championIconUrl(CHAMPIONS[placement.id].iconId)
        : ENEMIES[placement.id].iconPath;
      this.drawUnitOnHex(ctx, x, y, sz, "enemy", icon);
    }
  }

  private drawUnitOnHex(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    sz: number,
    team: "player" | "enemy",
    iconUrl: string,
  ): void {
    const img = this.game.assets.get(iconUrl);

    // Shadow circle
    ctx.save();
    ctx.translate(x, y + sz * 0.35);
    ctx.scale(1, 0.35);
    ctx.beginPath();
    ctx.arc(0, 0, sz * 0.5, 0, Math.PI * 2);
    ctx.fillStyle = team === "player" ? "rgba(74,158,255,0.45)" : "rgba(200,65,61,0.45)";
    ctx.fill();
    ctx.restore();

    if (img) {
      drawSprite(ctx, img, { x, y, width: sz, height: sz, pivotX: 0.5, pivotY: 0.5 });
    } else {
      // Placeholder circle
      ctx.save();
      ctx.fillStyle = team === "player" ? "#4a9eff" : "#c8413d";
      ctx.globalAlpha = 0.7;
      ctx.beginPath();
      ctx.arc(x, y, sz * 0.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
}

function keyToHex(key: string): HexCoord {
  const [q, r] = key.split(",").map(Number);
  return { q, r };
}
